import type { JournalLine } from "@/apis/gql/types";
import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type CreateJournalLineInput = {
  journalEntryId: string;
  journalAccountId: string;
  amount: number;
  side: "debit" | "credit";
  currency: string;
  timestamp: Date;
  taxCodeId?: string | null;
  costCenterId?: string | null;
};

const CREATE_JOURNAL_LINES_MUTATION = gql`
  mutation CreateJournalLine(
    $journalEntryId: String!
    $journalAccountId: String!
    $amount: Float!
    $side: String!
    $currency: String!
    $timestamp: DateType!
    $taxCodeId: String
    $costCenterId: String
  ) {
    createJournalLine(
      journalEntryId: $journalEntryId
      journalAccountId: $journalAccountId
      amount: $amount
      side: $side
      currency: $currency
      timestamp: $timestamp
      taxCodeId: $taxCodeId
      costCenterId: $costCenterId
    )
  }
`;

export const useCreateJournalLines = () => {
  const { t } = useTranslation();
  const [createJournalLines, { data, loading, error }] = useMutation(
    CREATE_JOURNAL_LINES_MUTATION,
    {
      refetchQueries: ["GetAllJournalEntries"],
    },
  );

  const handleCreateJournalLines = async (
    data: CreateJournalLineInput,
    onSuccess: (data: JournalLine) => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await createJournalLines({
        variables: {
          journalEntryId: data.journalEntryId,
          journalAccountId: data.journalAccountId,
          amount: data.amount,
          side: data.side,
          currency: data.currency,
          timestamp: data.timestamp,
          taxCodeId: data.taxCodeId,
          costCenterId: data.costCenterId,
        },
      });

      if (response.errors) {
        throw new Error("error creating journal lines");
      }

      onSuccess(response.data.createJournalLines);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleCreateJournalLines, { data, loading, error }] as const;
};
